import { isAfter } from 'date-fns';
import { IServiceWorkerConfig } from './dashboard.sw.interface';
import { FUEL_CACHE_TYPE, getTTL, getFuelExpiryTimeFromApi } from '../fuel/fuel.utils';

const TTL_FROM_API_KEY: string = 'FROM_API';

export const getExpiryTimestamp =
  (config: IServiceWorkerConfig, response: any, requestUrl: string, cacheType: string): string | null | undefined => {
    const ttl = config.CACHE_API_LIST.find(api => requestUrl.includes(api.endpoint))?.ttl || 0;
    if (cacheType === FUEL_CACHE_TYPE) {
      if (ttl && ttl === TTL_FROM_API_KEY) getFuelExpiryTimeFromApi(requestUrl, response, ttl);
      else return getTTL(ttl);
    } else {
      return ttl ? getTTL(ttl) : null;
    }
  };

export const fetchNewResponse =
  (request: Request, cacheName: string, cacheType: string, expiryKey: string, serviceWorkerConfig: IServiceWorkerConfig):
    Promise<Response> => {
    const promiseResponse: Promise<Response> = fetch(request.url, { headers: request.headers })
      .then((response: Response): Response => {
        if (response.ok) {
          storeResponseInCache(request, response.clone(), cacheName, cacheType, expiryKey, serviceWorkerConfig);
        }
        return response;
      });

    return Promise.resolve().then((): Promise<Response> => promiseResponse);
  };

const storeResponseInCache =
  (request: Request,
    response: Response,
    cacheName: string,
    cacheType: string,
    expiryKey: string,
    serviceWorkerConfig: IServiceWorkerConfig,
  ):
    void => {
    const clonedResponse = response.clone();
    clonedResponse.json()
      .then((data: any): void => {
        const clonedHeaders: Headers = new Headers(clonedResponse.headers);
        clonedHeaders.append(expiryKey, getExpiryTimestamp(serviceWorkerConfig, data, request.url, cacheType));
        const responseInit: ResponseInit = {
          status: clonedResponse.status,
          statusText: clonedResponse.statusText,
          headers: clonedHeaders,
        };
        const responseWithExpires: Response = new Response(response.body, responseInit);

        caches.open(cacheName)
          .then((cache: Cache): void => {
            cache.put(request, responseWithExpires);
          });
      });
  };

/**
 * Deletes the request and response after it is expired.
 * This function is only invoked when the request and response is not cached/expired.
 */
export const deleteExpiredResponses = (cacheName: string, cacheType: string): void => {
  caches.open(cacheName).then((cache: Cache): void => {
    cache.keys().then((requests: readonly Request[]): void => {
      for (const request of requests) {
        caches.match(request).then((cachedResponse: Response): void => {
          if (!isValidResponse(cachedResponse, cacheType)) {
            cache.delete(request);
          }
        });
      }
    });
  });
};

/**
 * Deletes the entire cache whenever there is a change in the service worker version.
 * Change the service worker version whenever there is a logical change in the Caching strategy.
 */
export const deleteOldCacheVersions = async (cacheName: string) => {
  const cacheNames = await caches.keys();
  const validCacheSet = new Set([cacheName]);

  await Promise.all(
    cacheNames.map(name => {
      if (!validCacheSet.has(name)) {
        return caches.delete(name);
      }
    }),
  );
};


export const isTimestampExpired = (timestamp: string): boolean => {
  return isAfter(new Date(), new Date(timestamp));
};

export const shouldCacheRequest = (cacheAPIList: Array<{ endpoint: string, ttl: number | string }>, requestURL: string) => {
  const list = cacheAPIList.map(api => api.endpoint);
  return list.some(item => requestURL.includes(item));
};

const isResponseExpired = (response: Response, expiryKey: string): boolean => {
  const expiresAt: string | null = response.headers.get(expiryKey);
  return isTimestampExpired(expiresAt as string);
};

export const isValidResponse = (response: Response, expiryKey: string): boolean => {
  if (!response || isResponseExpired(response, expiryKey)) return false;
  return true;
};

export const deleteCacheEntry = async (cacheName: string, requestUrl: string): Promise<void> => {
  if ('caches' in window) {
    try {
      const cache = await caches.open(cacheName);
      const requests = await cache.keys();
      for (const request of requests) {
        if (request.url.includes(requestUrl)) {
          try {
            await cache.delete(request);
          } catch (error) {
            console.error(`Error deleting cache entry ${requestUrl}:`, error);
          }
        }
      }
    } catch (error) {
      console.error(`Error opening cache ${cacheName}:`, error);
    }
  }
};